import { Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { buildDashboardDataPostUrl, isDashboardShareLinkActive } from '../dashboard-share-link-api';
import { postDashboardListData } from '../dashboard-data-api';
import { ListRequestDTO } from '../../../dto/list-request.dto';
import { ListResponseDTO } from '../../../dto/list-response.dto';

export interface SimpleNumberData {
  value: number | string;
  title?: string;
  subtitle?: string;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    percentage: number;
  };
}

function userProgressListRequest(request?: ListRequestDTO, pageSize = 10000): ListRequestDTO {
  return {
    page: 1,
    pageSize,
    filters: request?.filters ?? {},
    search: request?.search,
    sortBy: request?.sortBy,
    sortDir: request?.sortDir,
  };
}

function loadUsersViaDashboard(http: HttpClient, request?: ListRequestDTO, pageSize = 10000) {
  const effectivePageSize = Math.max(pageSize, request?.pageSize && request.pageSize > 1 ? request.pageSize : 10000);
  return postDashboardListData(http, 'userProgress', userProgressListRequest(request, effectivePageSize)).pipe(
    map((response) => ({
      users: response.data || [],
      totalItems: response.meta?.totalItems ?? (response.data?.length ?? 0),
    }))
  );
}

/**
 * Get simple number data for basic number widgets
 * This implementation fetches real user count from the API
 */
export function getSimpleNumberData(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  const pageSize = Math.max(request?.pageSize ?? 1000, 1000);
  return loadUsersViaDashboard(http, request, pageSize).pipe(
    map(({ users, totalItems }) => {
      const totalUsers = totalItems || users.length;
      
      
      const numberData: SimpleNumberData = {
        value: totalUsers,
        title: 'Total Brugere',
        subtitle: 'Alle registrerede brugere',
        trend: {
          direction: 'neutral',
          percentage: 0
        }
      };

      return {
        data: [numberData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('[SIMPLE-NUMBER-DATA] Error fetching user count:', error);
      if (isDashboardShareLinkActive()) {
        return of({
          data: [{
            value: 0,
            title: 'Total Brugere',
            subtitle: 'Kunne ikke hente data',
            trend: { direction: 'neutral', percentage: 0 }
          }],
          meta: { page: 1, pageSize: 1, totalItems: 1, totalPages: 1 }
        } as ListResponseDTO<SimpleNumberData>);
      }
      
      // Fallback to mock data if API fails
      const fallbackData: SimpleNumberData = {
        value: 0,
        title: 'Total Brugere',
        subtitle: 'Kunne ikke hente data',
        trend: {
          direction: 'neutral',
          percentage: 0
        }
      };

      return of({
        data: [fallbackData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>);
    })
  );
}

/**
 * Get user count data - now uses the same API call as getSimpleNumberData
 */
export function getUserCountData(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  
  // Use the same implementation as getSimpleNumberData
  return getSimpleNumberData(http, request).pipe(
    map((response: ListResponseDTO<SimpleNumberData>) => {
      // Modify the title to be more specific
      if (response.data && response.data.length > 0) {
        response.data[0].title = 'Aktive Brugere';
        response.data[0].subtitle = 'Brugere i systemet';
      }
      return response;
    })
  );
}

/**
 * Get completion rate data - calculates average completion from user data
 */
export function getCompletionRateData(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  return loadUsersViaDashboard(http, request).pipe(
    map(({ users }) => {
      // The backend already filters users by location for uddansvarlig
      let totalCompletion = 0;
      let userCount = 0;
      
      // Calculate average completion percentage from filtered users
      users.forEach((user: any) => {
        const completion = user.status?.registered_percentage ?? user.registeredPercentage ?? 0;
        totalCompletion += completion;
        userCount++;
      });
      
      const averageCompletion = userCount > 0 ? Math.round(totalCompletion / userCount) : 0;
      
      
      const completionData: SimpleNumberData = {
        value: averageCompletion,
        title: 'Gennemsnitlig Fremgang',
        subtitle: `Baseret på ${userCount} brugere`,
        trend: {
          direction: averageCompletion >= 50 ? 'up' : averageCompletion >= 25 ? 'neutral' : 'down',
          percentage: 0
        }
      };

      return {
        data: [completionData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('[SIMPLE-NUMBER-DATA] Error fetching completion rate:', error);
      
      // Fallback to mock data if API fails
      const fallbackData: SimpleNumberData = {
        value: 0,
        title: 'Gennemsnitlig Fremgang',
        subtitle: 'Kunne ikke hente data',
        trend: {
          direction: 'neutral',
          percentage: 0
        }
      };

      return of({
        data: [fallbackData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>);
    })
  );
}

/**
 * Get count of users with 100% completion
 */
export function getCompletedUsersCount(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  return loadUsersViaDashboard(http, request).pipe(
    map(({ users, totalItems }) => {
      
      // Count users with 100% completion
      // The backend already filters users by location for uddansvarlig, so we count from the filtered list
      const completedUsers = users.filter((user: any) => {
        const completion = user.status?.registered_percentage ?? user.registeredPercentage ?? 0;
        return completion >= 100;
      }).length;

      const totalUsers = totalItems || users.length;
      
      
      const completedData: SimpleNumberData = {
        value: completedUsers,
        title: 'Færdige Brugere',
        subtitle: `${totalUsers} brugere i alt`,
        trend: {
          direction: completedUsers > 0 ? 'up' : 'neutral',
          percentage: totalUsers > 0 ? Math.round((completedUsers / totalUsers) * 100) : 0
        }
      };

      return {
        data: [completedData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('[SIMPLE-NUMBER-DATA] Error fetching completed users count:', error);
      
      const fallbackData: SimpleNumberData = {
        value: 0,
        title: 'Færdige Brugere',
        subtitle: 'Kunne ikke hente data',
        trend: {
          direction: 'neutral',
          percentage: 0
        }
      };

      return of({
        data: [fallbackData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>);
    })
  );
}

/**
 * Get count of users with warnings (not ok_status)
 */
export function getUsersWithWarningsCount(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  return loadUsersViaDashboard(http, request).pipe(
    map(({ users, totalItems }) => {
      
      // Count users with warnings (not ok_status)
      // The backend already filters users by location for uddansvarlig, so we count from the filtered list
      const usersWithWarnings = users.filter((user: any) => {
        const status = user.status ?? {};
        return status.ok_status !== true && status.validation_status !== 'valid';
      }).length;

      const totalUsers = totalItems || users.length;
      
      
      const warningData: SimpleNumberData = {
        value: usersWithWarnings,
        title: 'Brugere med Advarsler',
        subtitle: `${totalUsers} brugere i alt`,
        trend: {
          direction: usersWithWarnings > 0 ? 'down' : 'up',
          percentage: totalUsers > 0 ? Math.round((usersWithWarnings / totalUsers) * 100) : 0
        }
      };

      return {
        data: [warningData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('[SIMPLE-NUMBER-DATA] Error fetching users with warnings count:', error);
      
      const fallbackData: SimpleNumberData = {
        value: 0,
        title: 'Brugere med Advarsler',
        subtitle: 'Kunne ikke hente data',
        trend: {
          direction: 'neutral',
          percentage: 0
        }
      };

      return of({
        data: [fallbackData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>);
    })
  );
}

/**
 * Get ratio of registered percentage to progress percentage
 */
export function getRegisteredToProgressRatio(
  http: HttpClient,
  request?: ListRequestDTO
): Observable<ListResponseDTO<SimpleNumberData>> {
  
  
  // Use the dedicated backend endpoint for organization-wide progress statistics
  const apiUrl = buildDashboardDataPostUrl('organizationProgress');
  
  // Create a dashboard data request
  const dashboardRequest = {
    sourceType: 'organizationProgress',
    filters: request?.filters || {},
    year: new Date().getFullYear() // Use current year, could be made configurable
  };
  
  return http.post(apiUrl, dashboardRequest).pipe(
    map((response: any) => {
      
      // Extract the organization-wide percentages from the response
      const data = response.data || [];
      const orgData = data.length > 0 ? data[0] : { registered_percentage: 0, progress_percentage: 0 };
      
      const registeredPercentage = orgData.registered_percentage || 0;
      const progressPercentage = orgData.progress_percentage || 0;
      
      
      // Display as "value1 / value2" format with better visual formatting
      const displayValue = `${registeredPercentage}% / ${progressPercentage}%`;
      
      
      const ratioData: SimpleNumberData = {
        value: displayValue as any, // Display the formatted string instead of a number
        title: 'Reg/Frem',
        subtitle: 'Registreret/Fremgang',
        trend: {
          direction: registeredPercentage >= progressPercentage ? 'up' : registeredPercentage >= (progressPercentage * 0.8) ? 'neutral' : 'down',
          percentage: 0 // No trend percentage since we're showing raw values
        }
      };

      return {
        data: [ratioData],
        meta: {
          page: 1,
          pageSize: 1,
          totalItems: 1,
          totalPages: 1
        }
      } as ListResponseDTO<SimpleNumberData>;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('[SIMPLE-NUMBER-DATA] Error fetching organization progress data:', error);
      if (isDashboardShareLinkActive()) {
        return of({
          data: [{
            value: '0% / 0%',
            title: 'Reg/Frem',
            subtitle: 'Registreret/Fremgang',
            trend: { direction: 'neutral' as const, percentage: 0 }
          }],
          meta: { page: 1, pageSize: 1, totalItems: 1, totalPages: 1 }
        } as ListResponseDTO<SimpleNumberData>);
      }
      console.error('[SIMPLE-NUMBER-DATA] Falling back to individual user calculation');
      return loadUsersViaDashboard(http, request).pipe(
        map(({ users }) => {
          let totalRegistered = 0;
          let totalProgress = 0;
          let userCount = 0;
          users.forEach((user: any) => {
            const status = user.status ?? {};
            totalRegistered += status.registered_percentage ?? user.registeredPercentage ?? 0;
            totalProgress += status.progress_percentage ?? user.percentComplete ?? 0;
            userCount++;
          });
          const registeredPercentage = userCount > 0 ? Math.round(totalRegistered / userCount) : 0;
          const progressPercentage = userCount > 0 ? Math.round(totalProgress / userCount) : 0;
          const displayValue = `${registeredPercentage}% / ${progressPercentage}%`;
          return {
            data: [{
              value: displayValue as any,
              title: 'Reg/Frem',
              subtitle: 'Registreret/Fremgang',
              trend: {
                direction: registeredPercentage >= progressPercentage ? 'up' : 'neutral',
                percentage: 0
              }
            }],
            meta: { page: 1, pageSize: 1, totalItems: 1, totalPages: 1 }
          } as ListResponseDTO<SimpleNumberData>;
        }),
        catchError(() => of({
          data: [{ value: '0% / 0%', title: 'Reg/Frem', subtitle: 'Kunne ikke hente data', trend: { direction: 'neutral' as const, percentage: 0 } }],
          meta: { page: 1, pageSize: 1, totalItems: 1, totalPages: 1 }
        } as ListResponseDTO<SimpleNumberData>))
      );
    })
  );
}
